import type React from "react";
import { useState } from "react";

import type { SelectProps } from "@mui/material";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { BLUE, DARK_BLUE } from "theme/colors";

import { MUIBorder, MUIDisabled, MUIErrorText, MUIInput, MUITextInputLabel } from "../StyledInputs";

const menuProps = {
  style: {
    maxHeight: 460,
  },
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontWeight: 500,
        color: DARK_BLUE[100],
        "&:hover": {
          backgroundColor: BLUE[20],
        },
        "&.Mui-selected": {
          backgroundColor: BLUE[10],
          "&:hover": {
            backgroundColor: BLUE[20],
          },
        },
      },
    },
  },
};

const MUIDropdownInForm: React.FC<Props> = ({
  className,
  error,
  label,
  name,
  options,
  renderItem,
  disabled,
  required,
  minWidth = 116,
  onChange,
  defaultValue = "",
}) => {
  const { control } = useFormContext();

  return (
    <Container className={className}>
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        defaultValue={defaultValue}
        render={({ field }) => (
          <StyledFormControl disabled={disabled} fullWidth>
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <StyledSelect
              $minWidth={minWidth}
              error={!!error}
              labelId={`${name}-label`}
              data-testid={`dropdown-${name}`}
              value={field.value ?? defaultValue}
              onChange={event => {
                field.onChange(event);
                if (onChange) onChange(event);
              }}
              MenuProps={menuProps}
              renderValue={() => {
                return <>{options.find(option => option.value === field.value)?.label}</>;
              }}
            >
              {options.map(option => (
                <MenuItem
                  key={option.value}
                  disabled={option.disabled}
                  value={option.value}
                  data-testid={`dropdown-option-${option.value}`}
                >
                  {renderItem ? renderItem(option) : `${option.label} ${option.customLabel ? option.customLabel : ""}`}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>
        )}
      />
      {!!error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

const MUIDropdownNoForm: React.FC<Props> = ({
  className,
  error,
  label,
  name,
  options,
  renderItem,
  disabled,
  minWidth = 116,
  value,
  onChange,
  defaultValue,
}) => {
  const [selectedValue, setSelectedValue] = useState<unknown>(value || options[0].value);

  return (
    <Container className={className}>
      <StyledFormControl disabled={disabled} fullWidth>
        <InputLabel id={`${name}-label`}>{label}</InputLabel>
        <StyledSelect
          $minWidth={minWidth}
          error={!!error}
          labelId={`${name}-label`}
          data-testid={`dropdown-${name}`}
          MenuProps={menuProps}
          value={selectedValue ?? defaultValue}
          onChange={event => {
            setSelectedValue(event.target.value);
            if (onChange) onChange(event);
          }}
        >
          {options.map(option => (
            <MenuItem
              key={option.value}
              disabled={option.disabled}
              value={option.value}
              data-testid={`dropdown-option-${option.value}`}
            >
              {renderItem ? renderItem(option) : option.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledFormControl>

      {!!error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

export const MUIDropdown: React.FC<Props> = props => {
  const { in_form = true } = props;
  if (in_form) {
    return <MUIDropdownInForm {...props} />;
  }
  return <MUIDropdownNoForm {...props} />;
};

const StyledFormControl = styled(FormControl)`
  .MuiInputBase-root {
    .MuiSelect-select {
      padding: 23px 12px 7px;
    }
  }
  ${MUIInput}
  ${MUIBorder}
  ${MUITextInputLabel}
  ${MUIDisabled}
`;

const StyledSelect = styled(Select)<{ $minWidth: number }>`
  .MuiSelect-select {
    background-color: white;
    min-width: ${props => `${props.$minWidth}px`};
  }
`;

const Container = styled.div`
  flex-wrap: wrap;
  margin-top: 0;
  width: 100%;
`;

const ErrorText = styled.div`
  margin-top: 4px;
  ${MUIErrorText}
`;

export interface Option {
  label: string;
  value: string | number;
  customLabel?: string;
  disabled?: boolean;
}

export interface Props {
  readonly error?: string;
  readonly label?: string;
  readonly name: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly in_form?: boolean;
  readonly options: Array<Option>;
  readonly className?: string;
  readonly minWidth?: number;
  readonly value?: string | number;
  readonly renderItem?: (option: Option) => JSX.Element;
  readonly onChange?: (event: Parameters<NonNullable<SelectProps["onChange"]>>[0]) => void;
  readonly defaultValue?: string;
}
